import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';

import { Main } from '@/components/layout/main';
import { PageHeader } from '@/components/layout/page-header';
import { Newspaper } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  Plus,
  Eye,
  Edit2,
  Trash2,
  MoreHorizontal,
  FileText,
  Search as SearchIcon,
} from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import {
  get_superadmin_blog_posts,
  delete_superadmin_blog_post,
  list_blog_domains,
  BlogPost,
  BlogDomain,
} from '@/api/blog';

import { handleServerError } from '@/utils/handle-server-error';

export default function BlogManagement() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [blogs, setBlogs] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [domains, setDomains] = useState<BlogDomain[]>([]);
  const [selectedDomain, setSelectedDomain] = useState<string>('all');

  useEffect(() => {
    fetchDomains();
    fetchBlogs();
  }, []);

  const fetchDomains = async () => {
    try {
      const data = await list_blog_domains();
      setDomains(data);
    } catch (error) {
      handleServerError(error);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, [selectedDomain]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const data = await get_superadmin_blog_posts(selectedDomain !== 'all' ? selectedDomain : undefined);
      setBlogs(data);
    } catch (error) {
      handleServerError(error);
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (blog: BlogPost) => {
    try {
      await delete_superadmin_blog_post(blog.id);
      fetchBlogs();
    } catch (error) {
      handleServerError(error);
    }
  };

  const filteredBlogs = blogs.filter((blog) =>
    blog.title.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex h-screen">
        <div className="flex-1 min-w-0">
          <Main>
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-3" />
              <p className="text-muted-foreground">{t('loading')}</p>
            </div>
          </Main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen">
      <div className="flex-1 min-w-0">
        <Main>
          <div className="space-y-6">
            <PageHeader
              leading={
                <span className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Newspaper className="h-5 w-5" />
                </span>
              }
              title={t('blog_management')}
            />

            {/* Toolbar */}
            <div className="flex items-center justify-between gap-4">
              <div className="relative max-w-sm flex-1">
                <SearchIcon className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('search_blogs')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8"
                />
              </div>

              <div className="flex items-center gap-2">
                <label className="text-sm font-medium whitespace-nowrap">{t('filter_by_domain', 'Domain:')}</label>
                <Select value={selectedDomain} onValueChange={setSelectedDomain}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder={t('select_domain', 'Select a domain')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('all_domains', 'All Domains')}</SelectItem>
                    {domains.map((dom) => (
                      <SelectItem key={dom.id} value={dom.name}>{dom.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                className="bg-slate-900 hover:bg-slate-800"
                onClick={() => navigate({ to: '/super-admin-blog-create' })}
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('add_blog')}
              </Button>
            </div>

            {/* Table */}
            {filteredBlogs.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <FileText className="h-12 w-12 text-slate-400 mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">
                    {search ? t('no_blogs_found') : t('no_blogs_yet')}
                  </h3>
                  <p className="text-slate-600 text-center max-w-md">
                    {search
                      ? t('try_different_search_terms')
                      : t('create_first_blog_to_get_started')}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('title')}</TableHead>
                        <TableHead>{t('blog_domain', 'Domain')}</TableHead>
                        <TableHead className="text-center">
                          {t('blog_status')}
                        </TableHead>
                        <TableHead>{t('category')}</TableHead>
                        <TableHead>{t('tags')}</TableHead>
                        <TableHead className="w-[120px] text-center">
                          {t('actions')}
                        </TableHead>
                      </TableRow>
                    </TableHeader>

                    <TableBody>
                      {filteredBlogs.map((blog) => (
                        <TableRow key={blog.id}>
                          {/* Title */}
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="font-medium">
                                {blog.title}
                              </span>
                              <span className="text-sm text-muted-foreground">
                                /{blog.slug}
                              </span>
                            </div>
                          </TableCell>

                          {/* Domain */}
                          <TableCell>
                            <span className="text-sm font-medium text-gray-600">
                              {blog.domain_name || '—'}
                            </span>
                          </TableCell>

                          {/* Status */}
                          <TableCell className="text-center">
                            <Badge
                              variant={
                                blog.is_published
                                  ? 'default'
                                  : 'secondary'
                              }
                            >
                              {blog.is_published
                                ? t('published')
                                : t('draft')}
                            </Badge>
                          </TableCell>

                          {/* Category */}
                          <TableCell>
                            {blog.category ? (
                              <Badge variant="outline">
                                {blog.category.name}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">
                                —
                              </span>
                            )}
                          </TableCell>

                          {/* Tags */}
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {blog.hashtags?.length ? (
                                blog.hashtags.map((tag) => (
                                  <Badge
                                    key={tag.id}
                                    variant="outline"
                                  >
                                    #{tag.name}
                                  </Badge>
                                ))
                              ) : (
                                <span className="text-muted-foreground">
                                  —
                                </span>
                              )}
                            </div>
                          </TableCell>

                          {/* Actions */}
                          <TableCell className="text-center">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  className="h-8 w-8 p-0"
                                >
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>

                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={() =>
                                    navigate({
                                      to: '/super-admin-blog-preview',
                                      search: { id: blog.id },
                                    })
                                  }
                                >
                                  <Eye className="mr-2 h-4 w-4" />
                                  {t('view')}
                                </DropdownMenuItem>

                                <DropdownMenuItem
                                  onClick={() =>
                                    navigate({
                                      to: '/super-admin-blog-edit',
                                      search: { id: blog.id },
                                    })
                                  }
                                >
                                  <Edit2 className="mr-2 h-4 w-4" />
                                  {t('edit')}
                                </DropdownMenuItem>

                                <DropdownMenuItem
                                  onClick={() => handleDelete(blog)}
                                  className="text-red-600 focus:text-red-600"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  {t('delete')}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </div>
        </Main>
      </div>
    </div>
  );
}
